<template>
  <v-card
    elevation="0"
    class="modern-events-card rounded-3xl overflow-hidden"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Header Section with Gradient Background -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" class="icon-events">mdi-calendar-multiple</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">Events Overview</h1>
          <p class="header-subtitle">Real-time event statistics and analytics</p>
        </div>
        <div class="header-actions">
          <v-btn
            icon
            size="small"
            variant="text"
            class="theme-toggle"
            @click="toggleTheme"
          >
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="chart-section">
      <div class="chart-container">
        <div class="chart-wrapper">
          <highcharts
            :options="chartOptions"
            class="modern-chart"
          ></highcharts>
        </div>
      </div>
    </div>

    <!-- Stats Section -->
    <div class="stats-section">
      <div class="stats-grid">
        <div
          class="stat-card stat-registered animate-in"
          @click="goToEvents('All')"
          style="--delay: 0s"
        >
          <div class="stat-icon">
            <v-icon color="primary" size="24">mdi-calendar</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter">{{ getEventCount.totalEvents || 0 }}</span>
            </div>
            <div class="stat-label">Total Events</div>
          </div>
          <div class="stat-trend">
            <v-icon color="success" size="16">mdi-trending-up</v-icon>
            <!-- <span class="trend-value">5.2%</span> -->
          </div>
        </div>

        <div
          class="stat-card stat-pending animate-in"
          @click="goToEvents('Individual')"
          style="--delay: 0.1s"
        >
          <div class="stat-icon">
            <v-icon color="warning" size="24">mdi-account</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter">{{ getEventCount.totalIndividualEvents || 0 }}</span>
            </div>
            <div class="stat-label">Individual Posts</div>
          </div>
          <div class="stat-trend">
            <v-icon color="success" size="16">mdi-trending-up</v-icon>
            <!-- <span class="trend-value">3.8%</span> -->
          </div>
        </div>

        <div
          class="stat-card stat-approved animate-in"
          @click="goToEvents('Competition')"
          style="--delay: 0.2s"
        >
          <div class="stat-icon">
            <v-icon color="success" size="24">mdi-trophy</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter">{{ getEventCount.totalCompetitionEvents || 0 }}</span>
            </div>
            <div class="stat-label">Competition Posts</div>
          </div>
          <div class="stat-trend">
            <v-icon color="error" size="16">mdi-trending-down</v-icon>
            <!-- <span class="trend-value">1.5%</span> -->
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      isDarkMode: false,
      chartOptions: {
        chart: {
          type: "areaspline",
          backgroundColor: 'transparent',
          borderRadius: 16,
          marginBottom: 30,
          marginLeft: 60,
          marginRight: 30,
          marginTop: 40,
          style: {
            fontFamily: 'Inter, system-ui, sans-serif'
          }
        },
        credits: {
          enabled: false
        },
        title: {
          text: null
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 0,
          lineWidth: 0,
          tickWidth: 0,
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            },
            formatter: function() {
              return moment(this.value).format("MMM DD");
            }
          }
        },
        yAxis: {
          title: { text: null },
          gridLineWidth: 1,
          gridLineColor: '#f1f5f9',
          labels: {
            style: {
              color: '#64748b',
              fontSize: '12px'
            }
          }
        },
        plotOptions: {
          areaspline: {
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, 'rgb(24, 90, 157)'],
                [1, 'rgba(24, 90, 157, 0.05)']
              ]
            },
            marker: {
              radius: 4,
              fillColor: '#185a9d',
              lineWidth: 2,
              lineColor: '#ffffff'
            },
            lineWidth: 3,
            states: {
              hover: {
                lineWidth: 3
              }
            },
            threshold: null
          }
        },
        series: [
          {
            name: "Events",
            data: this.eventGraphDatas,
            color: "#185a9d",
            marker: {
              enabled: true
            }
          }
        ],
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          borderRadius: 8,
          style: {
            color: '#ffffff',
            fontSize: '13px'
          },
          formatter: function() {
            return `<b>${moment(this.x).format("MMM DD, YYYY")}</b><br/>
                    Events: <b>${this.y}</b>`;
          }
        },
        legend: {
          enabled: false
        }
      }
    };
  },
  computed: {
    ...mapGetters("adminDashboard", ["getEventCount", "eventGraphData"]),
    eventGraphDatas() {
      return this.eventGraphData || [];
    }
  },
  methods: {
    ...mapActions("adminDashboard", [
      "fetchEventCountDetails",
      "fetchEventGraphDetails"
    ]),
    goToEvents(option) {
      this.$router.push({ name: "events", query: { option } });
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      document.documentElement.setAttribute('data-theme', this.isDarkMode ? 'dark' : 'light');
    }
  },
  watch: {
    eventGraphData(newData) {
      this.chartOptions.series[0].data = newData;
    }
  },
  mounted() {
    this.fetchEventGraphDetails();
    this.fetchEventCountDetails();
  }
};
</script>

<style scoped>
.modern-events-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  max-width: 100%;
  margin: 0;
  border-radius: 1.25rem;
}

.modern-events-card.dark-mode {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #185a9d  0%, #43cea2 100%);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>');
  background-size: 1.875rem 1.875rem;
  animation: float 20s infinite linear;
}

@keyframes float {
  0% { transform: translateY(0); }
  100% { transform: translateY(-1.875rem); }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.75rem;
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.icon-events {
  color: white !important;
}

.header-text {
  flex: 1;
  min-width: 200px;
}

.header-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin: 0.25rem 0 0 0;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.theme-toggle {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  color: white !important;
}

/* Chart Section */
.chart-section {
  padding: 1.5rem;
}

.chart-container {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 1.25rem;
  padding: 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
}

.dark-mode .chart-container {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-wrapper {
  min-height: 12.5rem;
  height: 100%;
  width: 100%;
}

.modern-chart {
  height: 100%;
  width: 100%;
}

/* Stats Section */
.stats-section {
  padding: 0 1.5rem 1.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 1.25rem;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(1.25rem);
}

.stat-card.animate-in {
  animation: slideUp 0.6s ease forwards;
  animation-delay: var(--delay);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark-mode .stat-card {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 0.1875rem;
  background: linear-gradient(90deg, transparent, var(--accent-color), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-card:hover {
  transform: translateY(-0.25rem);
  box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background: rgba(112, 36, 196, 0.1);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 3rem;
  height: 3rem;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-mode .stat-number {
  color: #f1f5f9;
}

.stat-label {
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 0.25rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark-mode .stat-label {
  color: #94a3b8;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.trend-value {
  color: inherit;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .header-actions {
    align-self: flex-end;
    margin-top: 0.5rem;
  }
  
  .header-text {
    min-width: 100%;
  }
  
  .header-title {
    font-size: 1.25rem;
  }
  
  .chart-section {
    padding: 1rem;
  }
  
  .chart-container {
    padding: 1rem;
    border-radius: 1rem;
  }
  
  .stats-section {
    padding: 0 1rem 1rem;
  }
  
  .stat-card {
    padding: 1rem;
  }
}

@media (max-width: 576px) {
  .header-section {
    padding: 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .modern-events-card {
    border-radius: 1rem;
  }
}

@media (max-width: 400px) {
  .header-title {
    font-size: 1.125rem;
  }
  
  .header-subtitle {
    font-size: 0.75rem;
  }
  
  .stat-icon {
    min-width: 2.5rem;
    height: 2.5rem;
    padding: 0.5rem;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
}
</style>